// src/api/services.js
// Every API call in the app goes through here.
// Import what you need: import { productsApi, ordersApi } from '@/api/services'
import api from './client'
import { executeRecaptcha } from './recaptcha'

// login / register / forgot-password auto-attach a v3 reCAPTCHA token;
// backend's enforceRecaptcha picks it up. Returns null when not configured.
const withRecaptcha = async (action, data) => {
  const recaptcha_token = await executeRecaptcha(action)
  return { ...data, recaptcha_token }
}

// ── Auth ─────────────────────────────────────────────────────────────────────
export const authApi = {
  user:           ()       => api.get('/user'),
  login:          async (d) => api.post('/auth/login',           await withRecaptcha('login',           d)),
  register:       async (d) => api.post('/auth/register',        await withRecaptcha('register',        d)),
  logout:         ()       => api.post('/auth/logout'),
  forgotPassword: async (d) => api.post('/auth/forgot-password', await withRecaptcha('forgot_password', d)),
  resetPassword:  (d)      => api.post('/auth/reset-password', d),
  verifyOtp:      (d)      => api.post('/auth/otp/verify', d),
  resendOtp:      ()       => api.post('/auth/otp/resend'),
  guestSendOtp:   (d)      => api.post('/auth/guest/send-otp', d),
  guestVerifyOtp: (d)      => api.post('/auth/guest/verify-otp', d),
  socialRedirect: (provider) => api.get(`/auth/social/${provider}/redirect`),
  socialCallback: (provider, code) => api.post(`/auth/social/${provider}/callback`, { code }),
}

// ── Products / Catalogue ────────────────────────────────────────────────────
export const productsApi = {
  list:           (params)    => api.get('/products', { params }),
  show:           (slug)      => api.get(`/products/${slug}`),
  categories:     ()          => api.get('/categories'),
  search:         (q, params) => api.get('/search', { params: { q, ...params } }),
  suggestions:    (q)         => api.get('/search/suggestions', { params: { q } }),
  compare:        (ids)       => api.get('/compare', { params: { ids: ids.join(',') } }),
  recentlyViewed: ()          => api.get('/recently-viewed'),
  recordView:     (productId) => api.post(`/products/${productId}/view`),
}

// ── Categories ────────────────────────────────────────────────────────────────
export const categoriesApi = {
  list: (params) => api.get('/categories', { params }),
  tree: ()       => api.get('/categories/tree'),  // route added server-side
}

// ── Search ────────────────────────────────────────────────────────────────────
// FIX P3: suggest was /search/suggest — server registers /search/suggestions
export const searchApi = {
  search:  (params) => api.get('/search', { params }),
  suggest: (q)      => api.get('/search/suggestions', { params: { q } }),
}

// ── Supplier: Products ───────────────────────────────────────────────────────
export const supplierProductsApi = {
  list:         (params)  => api.get('/supplier/products', { params }),
  show:         (id)      => api.get(`/supplier/products/${id}`),
  create:       (d)       => api.post('/supplier/products', d),
  update:       (id, d)   => api.put(`/supplier/products/${id}`, d),
  delete:       (id)      => api.delete(`/supplier/products/${id}`),
  submitReview: (id)      => api.post(`/supplier/products/${id}/submit-review`),
  uploadImages: (id, form)=> api.post(`/supplier/products/${id}/images`, form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteImage:  (imgId)   => api.delete(`/supplier/products/images/${imgId}`),
}

// ── Cart ─────────────────────────────────────────────────────────────────────
export const cartApi = {
  get:             ()       => api.get('/buyer/cart'),
  add:             (d)      => api.post('/buyer/cart', d),
  update:          (id, d)  => api.patch(`/buyer/cart/${id}`, d),
  remove:          (id)     => api.delete(`/buyer/cart/${id}`),
  clear:           ()       => api.delete('/buyer/cart'),
  applyCoupon:     (d)      => api.post('/buyer/cart/coupon', d),
  validateGiftCard:(d)      => api.post('/gift-cards/validate', d),
}

// ── Orders ───────────────────────────────────────────────────────────────────
export const ordersApi = {
  list:          (params) => api.get('/buyer/orders', { params }),
  show:          (id)     => api.get(`/buyer/orders/${id}`),
  place:         (d)      => api.post('/buyer/checkout', d),
  cancel:        (id)     => api.post(`/buyer/orders/${id}/cancel`),
  markDelivered: (id)     => api.post(`/buyer/orders/${id}/delivered`),
  invoice:       (id)     => api.get(`/buyer/orders/${id}/invoice`, { responseType: 'blob' }),
}

// ── Supplier Orders ──────────────────────────────────────────────────────────
export const supplierOrdersApi = {
  list:         (params) => api.get('/supplier/orders', { params }),
  show:         (id)     => api.get(`/supplier/orders/${id}`),
  markShipped:  (id, d)  => api.post(`/supplier/orders/${id}/ship`, d),
  updateStatus: (id, d)  => api.patch(`/supplier/orders/${id}/status`, d),
  invoice:      (id)     => api.get(`/supplier/orders/${id}/invoice`, { responseType: 'blob' }),
}

// ── Wishlists ────────────────────────────────────────────────────────────────
// FIX P1: shared was /wishlists/shared/ — server registers /buyer/wishlists/shared/
export const wishlistApi = {
  list:   ()      => api.get('/buyer/wishlists'),
  toggle: (d)     => api.post('/buyer/wishlists/toggle', d),
  shared: (token) => api.get(`/buyer/wishlists/shared/${token}`),
}

// ── Reviews ──────────────────────────────────────────────────────────────────
export const reviewsApi = {
  listFor:     (type, id, p = {}) => api.get(`/products/${id}/reviews`, { params: p }),
  stats:       (type, id)         => api.get(`/products/${id}/reviews`, { params: { stats_only: 1 } }),
  submit:      (d)                => api.post('/buyer/reviews', d),
  myReviews:   (p = {})           => api.get('/buyer/reviews', { params: p }),
  markHelpful: (id)               => api.post(`/reviews/${id}/helpful`),
  flag:        (id, d)            => api.post(`/reviews/${id}/flag`, d),
}

// ── Supplier Reviews ──────────────────────────────────────────────────────────
export const supplierReviewsApi = {
  list:  ()      => api.get('/supplier/reviews'),
  stats: ()      => api.get('/supplier/reviews/stats'),
  reply: (id, d) => api.post(`/supplier/reviews/${id}/reply`, d),
}

// ── Notifications ─────────────────────────────────────────────────────────────
export const notificationsApi = {
  list:        (p = {}) => api.get('/notifications', { params: p }),
  unreadCount: ()       => api.get('/notifications/unread-count'),
  markRead:    (id)     => api.patch(`/notifications/${id}/read`),
  markAllRead: ()       => api.patch('/notifications/mark-all-read'),
  remove:      (id)     => api.delete(`/notifications/${id}`),
  preferences: ()       => api.get('/notifications/preferences'),
  updatePrefs: (d)      => api.put('/notifications/preferences', d),
}

// ── Messaging ────────────────────────────────────────────────────────────────
export const messagingApi = {
  threads:     (role)        => api.get(`/${role}/messages`),
  thread:      (role, id)    => api.get(`/${role}/messages/${id}`),
  reply:       (role, id, d) => api.post(`/${role}/messages/${id}/reply`, d),
  newThread:   (d)           => api.post('/buyer/messages', d),
  archive:     (role, id)    => api.post(`/${role}/messages/${id}/archive`),
  unreadCount: (role)        => api.get(`/${role}/messages/unread-count`),
  inbox:       (params)      => api.get('/buyer/messages', { params }),
  show:        (id)          => api.get(`/buyer/messages/${id}`),
  unread:      ()            => api.get('/buyer/messages/unread-count'),
}

// ── Supplier Messaging ────────────────────────────────────────────────────────
export const supplierMessagingApi = {
  inbox:   (params) => api.get('/supplier/messages', { params }),
  show:    (id)     => api.get(`/supplier/messages/${id}`),
  reply:   (id, d)  => api.post(`/supplier/messages/${id}/reply`, d),
  archive: (id)     => api.post(`/supplier/messages/${id}/archive`),
}

// ── RFQs ─────────────────────────────────────────────────────────────────────
// FIX P1: list/show/cancel were using singular /buyer/rfq — server uses plural /buyer/rfqs
// FIX P1: supplierList quote was using /supplier/rfq — server uses /supplier/rfqs
export const rfqApi = {
  buyerList:      (params)         => api.get('/buyer/rfqs', { params }),
  buyerShow:      (id)             => api.get(`/buyer/rfqs/${id}`),
  create:         (d)              => api.post('/buyer/rfqs', d),
  cancel:         (id)             => api.delete(`/buyer/rfqs/${id}`),
  award:          (rfqId, quoteId) => api.post(`/buyer/rfqs/${rfqId}/award/${quoteId}`),
  convertToOrder: (rfqId, d)       => api.post(`/buyer/rfqs/${rfqId}/convert`, d),
  quotationPdf:   (rfqId)          => api.get(`/buyer/rfqs/${rfqId}/quotation-pdf`, { responseType: 'blob' }),
  counterOffer:   (rfqId, quoteId, d) => api.post(`/buyer/rfqs/${rfqId}/counter/${quoteId}`, d),
  supplierList:   (params)         => api.get('/supplier/rfqs', { params }),
  quote:          (rfqId, d)       => api.post(`/supplier/rfqs/${rfqId}/quote`, d),
  list:           (params)         => api.get('/buyer/rfqs', { params }),
  show:           (id)             => api.get(`/buyer/rfqs/${id}`),
}

// ── Supplier RFQ (dedicated) ──────────────────────────────────────────────────
export const supplierRfqApi = {
  dashboard:    ()                    => api.get('/supplier/rfq/dashboard'),
  board:        (params)              => api.get('/supplier/rfq/board', { params }),
  myQuotes:     (params)              => api.get('/supplier/rfq/quotes', { params }),
  show:         (rfqId)               => api.get(`/supplier/rfq/${rfqId}`),
  submitQuote:  (rfqId, d)            => api.post(`/supplier/rfqs/${rfqId}/quote`, d),
  counterOffer: (rfqId, quoteId, d)   => api.post(`/supplier/rfqs/${rfqId}/counter/${quoteId}`, d),
}

// ── Samples ──────────────────────────────────────────────────────────────────
export const samplesApi = {
  list:            (params) => api.get('/buyer/samples', { params }),
  store:           (d)      => api.post('/buyer/samples', d),
  show:            (id)     => api.get(`/buyer/samples/${id}`),
  markDelivered:   (id)     => api.post(`/buyer/samples/${id}/delivered`),
  submitFeedback:  (id, d)  => api.post(`/buyer/samples/${id}/feedback`, d),
}

// ── Supplier Samples (dedicated) ─────────────────────────────────────────────
export const supplierSamplesApi = {
  list:     (params) => api.get('/supplier/samples', { params }),
  show:     (id)     => api.get(`/supplier/samples/${id}`),
  approve:  (id, d)  => api.post(`/supplier/samples/${id}/approve`, d),
  reject:   (id, d)  => api.post(`/supplier/samples/${id}/reject`, d),
  dispatch: (id, d)  => api.post(`/supplier/samples/${id}/dispatch`, d),
}

// ── Supplier Storefront ───────────────────────────────────────────────────────
// FIX P1: update was POST — server expects PUT
// FIX P2: publish/unpublish routes added server-side
export const storefrontApi = {
  list:   (params) => api.get('/storefronts', { params }),
  show:   (slug)   => api.get(`/storefronts/${slug}`),
  edit:   ()       => api.get('/supplier/storefront'),
  update: (d)      => api.put('/supplier/storefront', d),
  index:  (params) => api.get('/storefronts', { params }),
}

export const supplierStorefrontApi = {
  show:      ()  => api.get('/supplier/storefront'),
  update:    (d) => api.put('/supplier/storefront', d),
  publish:   ()  => api.post('/supplier/storefront/publish'),
  unpublish: ()  => api.post('/supplier/storefront/unpublish'),
}

// ── Subscriptions ────────────────────────────────────────────────────────────
export const subscriptionApi = {
  plans:      () => api.get('/subscription/plans'),
  current:    () => api.get('/supplier/subscription'),
  checkout:   (d) => api.post('/supplier/subscription/order', d),
  activate:   (d) => api.post('/supplier/subscription/activate', d),
  cancel:     (id) => api.post(`/supplier/subscription/cancel/${id}`),
  startTrial: () => api.post('/supplier/subscription/trial'),
}

// ── Supplier Subscription (dedicated) ────────────────────────────────────────
export const supplierSubscriptionApi = {
  index:       ()    => api.get('/supplier/subscription'),
  createOrder: (d)   => api.post('/supplier/subscription/order', d),
  activate:    (d)   => api.post('/supplier/subscription/activate', d),
  startTrial:  ()    => api.post('/supplier/subscription/trial'),
  cancel:      (id)  => api.post(`/supplier/subscription/cancel/${id}`),
}

// ── Loyalty ──────────────────────────────────────────────────────────────────
// FIX P2: redeem route added server-side
export const loyaltyApi = {
  balance: ()       => api.get('/buyer/loyalty'),
  check:   (points) => api.get('/buyer/loyalty/check', { params: { points } }),
  redeem:  (d)      => api.post('/buyer/loyalty/redeem', d),
}

// ── Referrals ────────────────────────────────────────────────────────────────
export const referralsApi = {
  get:   () => api.get('/buyer/referrals'),
  stats: () => api.get('/buyer/referrals'),
}

export const referralApi = {
  myCode:      () => api.get('/referral/my-code'),
  myReferrals: () => api.get('/referral/my-referrals'),
  stats:       () => api.get('/referral/stats'),
}

// ── Gift Cards ───────────────────────────────────────────────────────────────
// FIX P2: redeem route added server-side
export const giftCardsApi = {
  list:     ()  => api.get('/buyer/gift-cards'),
  purchase: (d) => api.post('/buyer/gift-cards', d),
  validate: (d) => api.post('/gift-cards/validate', d),
  myCards:  ()  => api.get('/buyer/gift-cards'),
  redeem:   (d) => api.post('/buyer/gift-cards/redeem', d),
}

// ── Supplier Analytics ───────────────────────────────────────────────────────
export const analyticsApi = {
  overview: (params) => api.get('/supplier/analytics', { params }),
  earnings: (params) => api.get('/supplier/analytics/earnings', { params }),
}

export const supplierAnalyticsApi = {
  dashboard: (days = 30) => api.get('/supplier/analytics', { params: { days } }),
  snapshots: (days = 30) => api.get('/supplier/analytics/earnings', { params: { days } }),
}

// ── Supplier Payouts ─────────────────────────────────────────────────────────
export const payoutsApi = {
  list: (params) => api.get('/supplier/payouts', { params }),
}

export const supplierPayoutsApi = {
  index: ()   => api.get('/supplier/payouts'),
  // NOTE: show/detail endpoint not yet implemented on server
}

// ── Supplier Bulk Import ─────────────────────────────────────────────────────
// FIX P3: list was /supplier/import/jobs — server registers /supplier/import
// FIX P3: status was /supplier/import/jobs/{id}/status — server registers /supplier/import/{id}/status
export const importApi = {
  list:     ()     => api.get('/supplier/import'),
  upload:   (form) => api.post('/supplier/import', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  status:   (id)   => api.get(`/supplier/import/${id}/status`),
  template: ()     => api.get('/supplier/import/template', { responseType: 'blob' }),
}

export const bulkImportApi = {
  list:   ()    => api.get('/supplier/import'),
  upload: (fd)  => api.post('/supplier/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  status: (id)  => api.get(`/supplier/import/${id}/status`),
}

// ── Supplier Profile ──────────────────────────────────────────────────────────
// FIX P3: uploadDocument was /supplier/profile/document — server uses /supplier/profile/documents (plural)
// FIX P2: updatePassword route added server-side
export const supplierProfileApi = {
  show:           ()    => api.get('/supplier/profile'),
  update:         (d)   => api.put('/supplier/profile', d),
  updatePassword: (d)   => api.put('/supplier/profile/password', d),
  uploadDocument: (fd)  => api.post('/supplier/profile/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
}

// ── Supplier Dashboard ────────────────────────────────────────────────────────
export const supplierDashboardApi = {
  index: () => api.get('/supplier/'),
}

// ── Supplier ERP ─────────────────────────────────────────────────────────────
export const supplierErpApi = {
  index: () => api.get('/supplier/erp'),
}

// ── Supplier Smart Pricing ──────────────────────────────────────────────────
export const supplierPricingApi = {
  index:   ()   => api.get('/supplier/pricing'),
  compute: ()   => api.post('/supplier/pricing/compute'),
  accept:  (id) => api.post(`/supplier/pricing/${id}/accept`),
  dismiss: (id) => api.post(`/supplier/pricing/${id}/dismiss`),
}

// ── Supplier Demand Forecasting ──────────────────────────────────────────────
export const supplierForecastApi = {
  index:    ()   => api.get('/supplier/forecast'),
  category: (id) => api.get(`/supplier/forecast/category/${id}`),
  compute:  ()   => api.post('/supplier/forecast/compute'),
}

// ── Buyer Dashboard ──────────────────────────────────────────────────────────
export const buyerDashboardApi = {
  index: () => api.get('/buyer'),
}

// ── Buyer Profile ─────────────────────────────────────────────────────────────
// FIX P2: updatePassword route added server-side
export const profileApi = {
  update:         (d) => api.put('/buyer/profile', d),
  updatePassword: (d) => api.put('/buyer/profile/password', d),
}

// ── Buyer Address ─────────────────────────────────────────────────────────────
export const addressApi = {
  list:       ()       => api.get('/buyer/addresses'),
  create:     (d)      => api.post('/buyer/addresses', d),
  update:     (id, d)  => api.put(`/buyer/addresses/${id}`, d),
  delete:     (id)     => api.delete(`/buyer/addresses/${id}`),
  setDefault: (id)     => api.post(`/buyer/addresses/${id}/default`),
}

// ── Checkout ──────────────────────────────────────────────────────────────────
export const checkoutApi = {
  placeOrder:    (d) => api.post('/buyer/checkout', d),
  verifyPayment: (d) => api.post('/buyer/checkout/verify-payment', d),
}

// ── Storefront (public) ───────────────────────────────────────────────────────
export const compareApi = {
  compare: (params) => api.get('/compare', { params }),
}

// ── CMS (public) ──────────────────────────────────────────────────────────────
export const cmsPublicApi = {
  page: (slug)   => api.get(`/pages/${slug}`),
  blog: (params) => api.get('/blog', { params }),
  post: (slug)   => api.get(`/blog/${slug}`),
}

// ── Admin: Users ─────────────────────────────────────────────────────────────
// FIX P2: approve and impersonate routes added server-side
export const adminUsersApi = {
  list:        (params) => api.get('/admin/users', { params }),
  show:        (id)     => api.get(`/admin/users/${id}`),
  suspend:     (id, d)  => api.post(`/admin/users/${id}/suspend`, d),
  activate:    (id)     => api.post(`/admin/users/${id}/activate`),
  bulk:        (d)      => api.post('/admin/users/bulk', d),
  export:      ()       => api.get('/admin/users/export', { responseType: 'blob' }),
  approve:     (id)     => api.post(`/admin/users/${id}/approve`),
  impersonate: (id)     => api.post(`/admin/users/${id}/impersonate`),
}

// ── Admin: Orders ────────────────────────────────────────────────────────────
export const adminOrdersApi = {
  list:         (params) => api.get('/admin/orders', { params }),
  show:         (id)     => api.get(`/admin/orders/${id}`),
  transition:   (id, d)  => api.patch(`/admin/orders/${id}/status`, d),
  updateStatus: (id, d)  => api.patch(`/admin/orders/${id}/status`, d),
  cancel:       (id, d)  => api.post(`/admin/orders/${id}/cancel`, d),
  refunds:      (params) => api.get('/admin/orders/refunds', { params }),
  // NOTE: refund endpoint not yet implemented on server
}

// ── Admin: Products ──────────────────────────────────────────────────────────
// FIX P2: show and delete routes added server-side
export const adminProductsApi = {
  list:    (params) => api.get('/admin/products', { params }),
  show:    (id)     => api.get(`/admin/products/${id}`),
  approve: (id)     => api.post(`/admin/products/${id}/approve`),
  reject:  (id, d)  => api.post(`/admin/products/${id}/reject`, d),
  delete:  (id)     => api.delete(`/admin/products/${id}`),
}

// ── Admin: Coupons ───────────────────────────────────────────────────────────
// FIX P2: toggle route added server-side
export const adminCouponsApi = {
  list:   (params) => api.get('/admin/coupons', { params }),
  create: (d)      => api.post('/admin/coupons', d),
  update: (id, d)  => api.put(`/admin/coupons/${id}`, d),
  delete: (id)     => api.delete(`/admin/coupons/${id}`),
  toggle: (id)     => api.post(`/admin/coupons/${id}/toggle`),
}

// ── Admin: Payouts ───────────────────────────────────────────────────────────
// FIX P2: generate and markPaid routes added server-side
export const adminPayoutsApi = {
  list:     (params) => api.get('/admin/payouts', { params }),
  generate: (d)      => api.post('/admin/payouts/generate', d),
  markPaid: (id, d)  => api.post(`/admin/payouts/${id}/mark-paid`, d),
  // NOTE: process endpoint not yet implemented on server — use markPaid instead
}

// ── Admin: Fraud ─────────────────────────────────────────────────────────────
// FIX P3: show was /admin/fraud/user/{userId} — server registers /admin/fraud/{id}
export const adminFraudApi = {
  list:    (params) => api.get('/admin/fraud', { params }),
  show:    (id)     => api.get(`/admin/fraud/${id}`),
  update:  (id, d)  => api.patch(`/admin/fraud/${id}`, d),
  scan:    ()       => api.post('/admin/fraud/scan'),
  review:  (id, d)  => api.patch(`/admin/fraud/${id}`, d),
  runScan: (d)      => api.post('/admin/fraud/scan', d),
}

// ── Admin: Disputes ──────────────────────────────────────────────────────────
// FIX P2: assign route added server-side
export const adminDisputesApi = {
  list:         (params) => api.get('/admin/disputes', { params }),
  show:         (id)     => api.get(`/admin/disputes/${id}`),
  addMessage:   (id, d)  => api.post(`/admin/disputes/${id}/message`, d),
  resolve:      (id, d)  => api.post(`/admin/disputes/${id}/resolve`, d),
  // NOTE: no generic PATCH status endpoint on server — use resolve/assign instead
  assign:       (id, d)  => api.post(`/admin/disputes/${id}/assign`, d),
}

// ── Admin: Health ─────────────────────────────────────────────────────────────
export const adminHealthApi = {
  snapshots: () => api.get('/admin/health'),
}

// ── Admin: Dashboard ─────────────────────────────────────────────────────────
export const adminDashboardApi = {
  index: () => api.get('/admin/dashboard'),
}

// ── Admin: Analytics ──────────────────────────────────────────────────────────
export const adminAnalyticsApi = {
  overview: (params) => api.get('/admin/analytics', { params }),
  index:    (params) => api.get('/admin/analytics', { params }),
}

// ── Admin: Announcements ──────────────────────────────────────────────────────
// Shared (active banner + dismiss — all roles)
export const announcementsApi = {
  active:    ()        => api.get('/announcements/active'),
  dismiss:   (id)      => api.post(`/announcements/${id}/dismiss`),
  adminList: ()        => api.get('/admin/announcements'),
  create:    (d)       => api.post('/admin/announcements', d),
  update:    (id, d)   => api.put(`/admin/announcements/${id}`, d),
  delete:    (id)      => api.delete(`/admin/announcements/${id}`),
  toggle:    (id)      => api.post(`/admin/announcements/${id}/toggle`),
}

// Admin-namespaced version
export const adminAnnouncementsApi = {
  list:   ()       => api.get('/admin/announcements'),
  create: (d)      => api.post('/admin/announcements', d),
  update: (id, d)  => api.put(`/admin/announcements/${id}`, d),
  delete: (id)     => api.delete(`/admin/announcements/${id}`),
  toggle: (id)     => api.post(`/admin/announcements/${id}/toggle`),
}

// ── Admin: CMS ───────────────────────────────────────────────────────────────
export const adminCmsApi = {
  list:      (params) => api.get('/admin/cms', { params }),
  show:      (id)     => api.get(`/admin/cms/${id}`),
  create:    (d)      => api.post('/admin/cms', d),
  update:    (id, d)  => api.put(`/admin/cms/${id}`, d),
  delete:    (id)     => api.delete(`/admin/cms/${id}`),
  publish:   (id)     => api.post(`/admin/cms/${id}/publish`),
  unpublish: (id)     => api.post(`/admin/cms/${id}/unpublish`),
}

// ── Admin: Impersonation ──────────────────────────────────────────────────────
export const adminImpersonationApi = {
  list:        ()    => api.get('/admin/impersonation'),
  impersonate: (d)   => api.post('/admin/impersonation/start', d),
  stop:        ()    => api.post('/admin/impersonation/stop'),
}

// ── Admin: Audit ──────────────────────────────────────────────────────────────
export const adminAuditApi = {
  list: (params) => api.get('/admin/audit', { params }),
}

// ── Admin: Loyalty ────────────────────────────────────────────────────────────
// FIX P2: adjust and saveConfig routes added server-side
export const adminLoyaltyApi = {
  index:      ()    => api.get('/admin/loyalty'),
  adjust:     (d)   => api.post('/admin/loyalty/adjust', d),
  saveConfig: (d)   => api.post('/admin/loyalty/config', d),
}

// ── Admin: Gift Cards ─────────────────────────────────────────────────────────
// FIX P2: issue and void routes added server-side
export const adminGiftCardsApi = {
  list:  (params) => api.get('/admin/gift-cards', { params }),
  issue: (d)      => api.post('/admin/gift-cards', d),
  void:  (id)     => api.post(`/admin/gift-cards/${id}/void`),
}

// ── Admin: Commissions ────────────────────────────────────────────────────────
export const adminCommissionsApi = {
  list:         ()          => api.get('/admin/commissions'),
  update:       (catId, d)  => api.put(`/admin/commissions/${catId}`, d),
  updateGlobal: (d)         => api.put('/admin/commissions/global', d),
}

// ── Admin: Referrals ──────────────────────────────────────────────────────────
export const adminReferralsApi = {
  index: () => api.get('/admin/referrals'),
}

// ── Admin: Abandoned Carts ────────────────────────────────────────────────────
// FIX P2: processDue route added server-side
export const adminAbandonedCartsApi = {
  index:      ()  => api.get('/admin/abandoned-carts'),
  processDue: ()  => api.post('/admin/abandoned-carts/process'),
}

// ── Admin: Subscriptions ──────────────────────────────────────────────────────
// FIX P3: subscribers was /admin/subscriptions/subscribers — server registers GET /admin/subscriptions
export const adminSubscriptionsApi = {
  plans:       ()         => api.get('/admin/subscriptions/plans'),
  subscribers: (params)   => api.get('/admin/subscriptions', { params }),
  savePlan:    (id, d)    => id ? api.put(`/admin/subscriptions/plans/${id}`, d) : api.post('/admin/subscriptions/plans', d),
}

// ── Admin: Promotions ─────────────────────────────────────────────────────────
export const adminPromotionsApi = {
  list:   ()       => api.get('/admin/promotions'),
  create: (d)      => api.post('/admin/promotions', d),
  update: (id, d)  => api.put(`/admin/promotions/${id}`, d),
  delete: (id)     => api.delete(`/admin/promotions/${id}`),
  // NOTE: toggle endpoint not yet implemented on server
}

// ── Admin: Reviews ────────────────────────────────────────────────────────────
export const adminReviewsApi = {
  list:     (params) => api.get('/admin/reviews', { params }),
  stats:    ()       => api.get('/admin/reviews/stats'),
  show:     (id)     => api.get(`/admin/reviews/${id}`),
  approve:  (id)     => api.post(`/admin/reviews/${id}/approve`),
  reject:   (id)     => api.post(`/admin/reviews/${id}/reject`),
  delete:   (id)     => api.delete(`/admin/reviews/${id}`),
  destroy:  (id)     => api.delete(`/admin/reviews/${id}`),
  moderate: (id, d)  => api.patch(`/admin/growth/reviews/${id}/moderate`),
  index:    (params) => api.get('/admin/reviews', { params }),
}

// ── Admin: Supplier Verification (M1) ────────────────────────────────────────
// FIX P1: all paths fixed from /verification → /kyc to match server convention
// FIX P2: scorecard and recomputeScorecard routes added server-side
export const supplierVerificationApi = {
  list:               (params) => api.get('/admin/suppliers/kyc/pending', { params }),
  show:               (id)     => api.get(`/admin/suppliers/${id}/kyc`),
  approve:            (id, d)  => api.post(`/admin/suppliers/${id}/kyc/approve`, d),
  reject:             (id, d)  => api.post(`/admin/suppliers/${id}/kyc/reject`, d),
  requestDocs:        (id, d)  => api.post(`/admin/suppliers/${id}/kyc/request-docs`, d),
  verifyGstin:        (id)     => api.post(`/admin/suppliers/${id}/gstin/verify`),
  verifyMsme:         (id, d)  => api.post(`/admin/suppliers/${id}/msme/verify`, d),
  scorecard:          (id)     => api.get(`/admin/suppliers/${id}/scorecard`),
  recomputeScorecard: (id)     => api.post(`/admin/suppliers/${id}/recompute-scorecard`),
}

// ── Supplier KYC (self-service) ───────────────────────────────────────────────
export const supplierKycApi = {
  submitForReview: () => api.post('/supplier/profile/kyc/submit'),
  getScorecard:    () => api.get('/supplier/scorecard'),
}

// ── Public: Supplier Scorecard ────────────────────────────────────────────────
export const scorecardApi = {
  get: (slug) => api.get(`/storefronts/${slug}/scorecard`),
}

// ── Currency ─────────────────────────────────────────────────────────────────
export const currencyApi = {
  rates: () => api.get('/currency/rates'),
}

// ── Tenant Config (public) ────────────────────────────────────────────────────
export const tenantConfigApi = {
  get: () => api.get('/tenant/config'),
}

// ── Tenant Admin ──────────────────────────────────────────────────────────────
export const tenantAdminApi = {
  dashboard:          ()        => api.get('/tenant/admin/dashboard'),
  getSettings:        ()        => api.get('/tenant/admin/settings'),
  updateSettings:     (d)       => api.put('/tenant/admin/settings', d),
  getBranding:        ()        => api.get('/tenant/admin/branding'),
  updateBranding:     (d)       => api.put('/tenant/admin/branding', d),
  uploadLogo:         (fd)      => api.post('/tenant/admin/branding/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadFavicon:      (fd)      => api.post('/tenant/admin/branding/favicon', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getDomains:         ()        => api.get('/tenant/admin/domains'),
  addDomain:          (d)       => api.post('/tenant/admin/domains', d),
  domainInstructions: (id)      => api.get(`/tenant/admin/domains/${id}/instructions`),
  verifyDomain:       (id)      => api.post(`/tenant/admin/domains/${id}/verify`),
  makePrimary:        (id)      => api.post(`/tenant/admin/domains/${id}/make-primary`),
  removeDomain:       (id)      => api.delete(`/tenant/admin/domains/${id}`),
  getTeam:            ()        => api.get('/tenant/admin/team'),
  inviteMember:       (d)       => api.post('/tenant/admin/team', d),
  updateRole:         (uid, d)  => api.put(`/tenant/admin/team/${uid}/role`, d),
  removeMember:       (uid)     => api.delete(`/tenant/admin/team/${uid}`),
  getBilling:         ()        => api.get('/tenant/admin/billing'),
  createUpgrade:      (d)       => api.post('/tenant/admin/billing/upgrade-order', d),
  confirmPayment:     (d)       => api.post('/tenant/admin/billing/confirm-payment', d),
}

// ── Super Admin ───────────────────────────────────────────────────────────────
export const superAdminServices = {
  listTenants:       (p)      => api.get('/super-admin/tenants', { params: p }),
  tenantStats:       ()       => api.get('/super-admin/tenants/stats'),
  platformAnalytics: (p)      => api.get('/super-admin/tenants/analytics', { params: p }),
  getTenant:         (id)     => api.get(`/super-admin/tenants/${id}`),
  provisionTenant:   (d)      => api.post('/super-admin/tenants', d),
  updatePlan:        (id, d)  => api.put(`/super-admin/tenants/${id}/plan`, d),
  suspendTenant:     (id, d)  => api.post(`/super-admin/tenants/${id}/suspend`, d),
  unsuspendTenant:   (id)     => api.post(`/super-admin/tenants/${id}/unsuspend`),
  impersonateTenant: (id)     => api.post(`/super-admin/tenants/${id}/impersonate`),
  deleteTenant:      (id)     => api.delete(`/super-admin/tenants/${id}`, { data: { confirm: 'DELETE' } }),
}

// ── Returns (Phase 4) ────────────────────────────────────────────────────
export const returnsApi = {
  list:   ()  => api.get('/buyer/returns'),
  show:   (id) => api.get(`/buyer/returns/${id}`),
  create: (d) => api.post('/buyer/returns', d),
}

// ── Support Tickets (Phase 4) ────────────────────────────────────────────
export const ticketsApi = {
  list:   (params) => api.get('/buyer/tickets', { params }),
  show:   (id)     => api.get(`/buyer/tickets/${id}`),
  create: (d)      => api.post('/buyer/tickets', d),
  reply:  (id, d)  => api.post(`/buyer/tickets/${id}/reply`, d),
}

// ── FAQ (Phase 4) ────────────────────────────────────────────────────────
export const faqApi = {
  list:    (params) => api.get('/faqs', { params }),
  helpful: (id)     => api.post(`/faqs/${id}/helpful`),
}

// ── Admin: Returns (Phase 4) ─────────────────────────────────────────────
export const adminReturnsApi = {
  list:       (params) => api.get('/admin/returns', { params }),
  show:       (id)     => api.get(`/admin/returns/${id}`),
  approve:    (id, d)  => api.post(`/admin/returns/${id}/approve`, d),
  reject:     (id, d)  => api.post(`/admin/returns/${id}/reject`, d),
  transition: (id, d)  => api.post(`/admin/returns/${id}/transition`, d),
}

// ── Admin: Support Tickets (Phase 4) ─────────────────────────────────────
export const adminTicketsApi = {
  list:         (params) => api.get('/admin/tickets', { params }),
  show:         (id)     => api.get(`/admin/tickets/${id}`),
  reply:        (id, d)  => api.post(`/admin/tickets/${id}/reply`, d),
  updateStatus: (id, d)  => api.patch(`/admin/tickets/${id}/status`, d),
  assign:       (id, d)  => api.post(`/admin/tickets/${id}/assign`, d),
}

// ── Admin: FAQs (Phase 4) ────────────────────────────────────────────────
export const adminFaqsApi = {
  list:   ()       => api.get('/admin/faqs'),
  create: (d)      => api.post('/admin/faqs', d),
  update: (id, d)  => api.put(`/admin/faqs/${id}`, d),
  delete: (id)     => api.delete(`/admin/faqs/${id}`),
}

// ── Supplier RFQ Counter-Offer (Phase 4) ─────────────────────────────────
export const supplierRfqNegotiateApi = {
  counterOffer: (rfqId, quoteId, d) => api.post(`/supplier/rfqs/${rfqId}/counter/${quoteId}`, d),
}

// ── Compliance: Admin E-way Bills + TCS ──────────────────────────────────
export const adminComplianceApi = {
  ewayBills:      (params) => api.get('/admin/eway-bills', { params }),
  ewayBillDraft:  (orderId)=> api.post(`/admin/eway-bills/generate/${orderId}`),
  ewayBillSubmit: (id)     => api.post(`/admin/eway-bills/${id}/submit`),
  ewayBillCancel: (id, d)  => api.post(`/admin/eway-bills/${id}/cancel`, d),
  tcsSummary:     (params) => api.get('/admin/tcs/summary', { params }),
  tcsTransactions:(params) => api.get('/admin/tcs/transactions', { params }),
  tcsRemit:       (d)      => api.post('/admin/tcs/remit', d),
}

// ── Fabrinza Textile Index (public) ──────────────────────────────────────
export const textileIndexApi = {
  index:         ()            => api.get('/textile-index'),
  categoryTrend: (id, params)  => api.get(`/textile-index/category/${id}`, { params }),
  api:           ()            => api.get('/textile-index/api'),
}

// ── GI Tag Premium Marketplace ────────────────────────────────────────────
export const giMarketplaceApi = {
  list:  (params) => api.get('/gi-marketplace', { params }),
  stats: ()       => api.get('/gi-marketplace/stats'),
  tags:  ()       => api.get('/gi-marketplace/tags'),
}

// ── Swatch Subscription ──────────────────────────────────────────────────
export const swatchSubscriptionApi = {
  index:             ()        => api.get('/buyer/swatch-subscription'),
  subscribe:         (d)       => api.post('/buyer/swatch-subscription', d),
  updatePreferences: (d)       => api.put('/buyer/swatch-subscription/preferences', d),
  cancel:            ()        => api.post('/buyer/swatch-subscription/cancel'),
  boxes:             (params)  => api.get('/buyer/swatch-subscription/boxes', { params }),
  feedback:          (boxId,d) => api.post(`/buyer/swatch-subscription/boxes/${boxId}/feedback`, d),
}

// ── AI Design Assistant ──────────────────────────────────────────────────
export const designAssistantApi = {
  generate:      (d)         => api.post('/buyer/ai/design-assistant', d),
  history:       ()          => api.get('/buyer/ai/design-assistant/history'),
  markProceeded: (sessionId) => api.post(`/buyer/ai/design-assistant/${sessionId}/proceeded`),
}

// ── Buyer Tier (Designer & Brand Network) ────────────────────────────────
export const buyerTierApi = {
  show:    ()  => api.get('/buyer/tier'),
  upgrade: (d) => api.post('/buyer/tier/upgrade', d),
}

// ── Insurance ────────────────────────────────────────────────────────────
export const insuranceApi = {
  list:     (params) => api.get('/buyer/insurance', { params }),
  plans:    (params) => api.get('/buyer/insurance/plans', { params }),
  purchase: (d)      => api.post('/buyer/insurance/purchase', d),
  fileClaim:(d)      => api.post('/buyer/insurance/claim', d),
  claims:   (params) => api.get('/buyer/insurance/claims', { params }),
}

// ── Compliance: Buyer GST Credits ────────────────────────────────────────
export const buyerGstCreditsApi = {
  ledger:       (params) => api.get('/buyer/gst-credits', { params }),
  monthly:      (params) => api.get('/buyer/gst-credits/monthly', { params }),
  markClaimed:  (d)      => api.post('/buyer/gst-credits/mark-claimed', d),
}

// ── Admin: Reports (Phase 6) ────────────────────────────────────────────────
export const adminReportsApi = {
  sales:               (params) => api.get('/admin/reports/sales', { params }),
  gst:                 (params) => api.get('/admin/reports/gst', { params }),
  supplierPerformance: (params) => api.get('/admin/reports/supplier-performance', { params }),
}

// ── M2: Supplier Market Intelligence ─────────────────────────────────────────
export const marketInsightsApi = {
  report:          ()    => api.get('/supplier/market-insights'),
  trends:          ()    => api.get('/supplier/market-insights/trends'),
  categorySignals: (id)  => api.get(`/supplier/market-insights/category/${id}`),
}

// ── M2: Buyer Personalization ─────────────────────────────────────────────────
export const personalizationApi = {
  recommendations: (params) => api.get('/buyer/recommendations', { params }),
  recentlyViewed:  ()       => api.get('/buyer/recently-viewed'),
}

// ── M3: AI Services ───────────────────────────────────────────────────────────
export const aiApi = {
  reorderPredictions: (days = 30) => api.get(`/buyer/ai/reorder-predictions?days_ahead=${days}`),
  chat:               (d)         => api.post('/buyer/ai/chat', d),
  chatHistory:        (sid)       => api.get(`/buyer/ai/chat-history?session_id=${sid}`),
  rfqMatches:         (rfqId)     => api.get(`/buyer/ai/rfq-matches/${rfqId}`),
}

export const aiSupplierApi = {
  generateDescription: (productId) => api.post('/supplier/ai/generate-description', { product_id: productId }),
  acceptDescription:   (id)        => api.post(`/supplier/ai/descriptions/${id}/accept`),
  demandForecast:      ()          => api.get('/supplier/ai/demand-forecast'),
}

export const aiAdminApi = {
  generateDescription: (productId) => api.post('/admin/ai/generate-description', { product_id: productId }),
  acceptDescription:   (id)        => api.post(`/admin/ai/descriptions/${id}/accept`),
  listDescriptions:    (params)    => api.get('/admin/ai/descriptions', { params }),
  fraudScore:          (userId)    => api.get(`/admin/ai/fraud-score/${userId}`),
}

export const aiSearchApi = {
  search:      (q, params = {})          => api.get('/ai-search', { params: { q, ...params } }),
  recordClick: (searchEventId, productId) => api.post('/search/click', { search_event_id: searchEventId, product_id: productId }),
}

// ── Visual / Image-based Fabric Search (M3) ───────────────────────────────
export const fabricSearchApi = {
  upload:  (formData) => api.post('/fabric-search/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  byUrl:   (imageUrl) => api.post('/fabric-search/url', { image_url: imageUrl }),
  history: ()         => api.get('/buyer/fabric-search/history'),
}

// ── M4: Finance Services ──────────────────────────────────────────────────────
export const financeApi = {
  buyer: {
    overview:         ()                   => api.get('/buyer/finance/overview'),
    creditAccount:    ()                   => api.get('/buyer/finance/credit-account'),
    applyCredit:      ()                   => api.post('/buyer/finance/apply-credit'),
    bnplOrders:       (p = {})             => api.get('/buyer/finance/bnpl-orders', { params: p }),
    installments:     (s)                  => api.get('/buyer/finance/installments', { params: { status: s } }),
    repayInstallment: (id, ref, method)    => api.post(`/buyer/finance/installments/${id}/repay`, { payment_reference: ref, payment_method: method }),
    transactions:     ()                   => api.get('/buyer/finance/transactions'),
    bnplEligibility:  (amount)             => api.get('/buyer/checkout/bnpl-eligibility', { params: { amount } }),
  },
  supplier: {
    overview:              ()        => api.get('/supplier/finance/overview'),
    eligibleOrders:        ()        => api.get('/supplier/finance/eligible-orders'),
    myRequests:            ()        => api.get('/supplier/finance/discount-requests'),
    submitDiscountRequest: (orderId) => api.post('/supplier/finance/discount-requests', { order_id: orderId }),
    cancelDiscountRequest: (id)      => api.delete(`/supplier/finance/discount-requests/${id}`),
  },
  admin: {
    overview:               ()          => api.get('/admin/finance/overview'),
    creditAccounts:         (p = {})    => api.get('/admin/finance/credit-accounts', { params: p }),
    approveCredit:          (bid, data) => api.post(`/admin/finance/credit-accounts/${bid}/approve`, data),
    adjustLimit:            (bid, data) => api.patch(`/admin/finance/credit-accounts/${bid}/adjust-limit`, data),
    suspendCredit:          (bid, r)    => api.post(`/admin/finance/credit-accounts/${bid}/suspend`, { reason: r }),
    reinstateCredit:        (bid)       => api.post(`/admin/finance/credit-accounts/${bid}/reinstate`),
    discountRequests:       (p = {})    => api.get('/admin/finance/discount-requests', { params: p }),
    approveDiscountRequest: (id)        => api.post(`/admin/finance/discount-requests/${id}/approve`),
    disburseDiscountRequest:(id, utr)   => api.post(`/admin/finance/discount-requests/${id}/disburse`, { utr }),
    rejectDiscountRequest:  (id, reason)=> api.post(`/admin/finance/discount-requests/${id}/reject`, { reason }),
    transactions:           (p = {})    => api.get('/admin/finance/transactions', { params: p }),
  },
}

// ── M5: Vertical Integration ──────────────────────────────────────────────────
export const warehouseApi = {
  overview:    ()        => api.get('/supplier/warehouses/overview'),
  list:        ()        => api.get('/supplier/warehouses'),
  create:      (d)       => api.post('/supplier/warehouses', d),
  update:      (id, d)   => api.put(`/supplier/warehouses/${id}`, d),
  setDefault:  (id)      => api.post(`/supplier/warehouses/${id}/default`),
  upsertStock: (id, d)   => api.post(`/supplier/warehouses/${id}/stock`, d),
  transfer:    (d)       => api.post('/supplier/warehouses/transfer', d),
}

export const qualityApi = {
  summary: ()       => api.get('/supplier/quality/summary'),
  list:    (p = {}) => api.get('/supplier/quality', { params: p }),
  request: (d)      => api.post('/supplier/quality', d),
  show:    (id)     => api.get(`/supplier/quality/${id}`),
}

export const shippingApi = {
  dashboard: ()           => api.get('/supplier/shipping/dashboard'),
  rates:     (d)          => api.post('/supplier/shipping/rates', d),
  book:      (orderId, d) => api.post(`/supplier/shipping/book/${orderId}`, d),
  tracking:  (orderId)    => api.get(`/supplier/shipping/tracking/${orderId}`),
}

export const rawMaterialApi = {
  market:         (p = {}) => api.get('/raw-materials', { params: p }),
  create:         (d)      => api.post('/raw-materials', d),
  update:         (id, d)  => api.put(`/raw-materials/${id}`, d),
  myListings:     ()       => api.get('/raw-materials/my-listings'),
  myOrders:       ()       => api.get('/raw-materials/my-orders'),
  incomingOrders: ()       => api.get('/raw-materials/incoming-orders'),
  placeOrder:     (id, d)  => api.post(`/raw-materials/order/${id}`, d),
  acceptOrder:    (id)     => api.post(`/raw-materials/order/${id}/accept`),
}

export const manufacturingApi = {
  buyer: {
    list:   ()       => api.get('/buyer/manufacturing'),
    place:  (d)      => api.post('/buyer/manufacturing', d),
    show:   (id)     => api.get(`/buyer/manufacturing/${id}`),
    accept: (id, d)  => api.post(`/buyer/manufacturing/${id}/accept`, d),
    cancel: (id, r)  => api.post(`/buyer/manufacturing/${id}/cancel`, { reason: r }),
  },
  supplier: {
    list:       ()       => api.get('/supplier/manufacturing'),
    show:       (id)     => api.get(`/supplier/manufacturing/${id}`),
    quote:      (id, d)  => api.post(`/supplier/manufacturing/${id}/quote`, d),
    transition: (id, d)  => api.post(`/supplier/manufacturing/${id}/transition`, d),
    cancel:     (id, r)  => api.post(`/supplier/manufacturing/${id}/cancel`, { reason: r }),
  },
}

export const exportDocApi = {
  list:     (orderId)        => api.get(`/supplier/export-docs/${orderId}`),
  generate: (orderId, type)  => api.post(`/supplier/export-docs/${orderId}`, { document_type: type }),
  finalise: (docId)          => api.post(`/supplier/export-docs/${docId}/finalise`),
}

export const adminLogisticsApi = {
  overview:        ()       => api.get('/admin/logistics/overview'),
  qcRequests:      (p = {}) => api.get('/admin/logistics/qc-requests', { params: p }),
  assignInspector: (id, d)  => api.post(`/admin/logistics/qc-requests/${id}/assign`, d),
  submitReport:    (id, d)  => api.post(`/admin/logistics/qc-requests/${id}/report`, d),
  shipments:       (p = {}) => api.get('/admin/logistics/shipments', { params: p }),
  syncTracking:    (id)     => api.post(`/admin/logistics/shipments/${id}/sync`),
}

// ── M6: International Expansion ───────────────────────────────────────────────
export const internationalApi = {
  locale:             ()    => api.get('/international/locale'),
  countries:          ()    => api.get('/international/countries'),
  taxEstimate:        (d)   => api.post('/buyer/international/tax-estimate', d),
  displayPrice:       (p)   => api.get('/buyer/international/display-price', { params: p }),
  stripeCreateIntent: (d)   => api.post('/buyer/international/stripe/create-intent', d),
  stripeVerify:       (d)   => api.post('/buyer/international/stripe/verify', d),
  paypalCreateOrder:  (d)   => api.post('/buyer/international/paypal/create-order', d),
  paypalCapture:      (d)   => api.post('/buyer/international/paypal/capture', d),
}

export const exportCertApi = {
  summary:             ()   => api.get('/supplier/export/summary'),
  certifications:      ()   => api.get('/supplier/export/certifications'),
  addCert:             (d)  => api.post('/supplier/export/certifications', d),
  customsSummary:      ()   => api.get('/supplier/export/customs/summary'),
  generateDeclaration: (d)  => api.post('/supplier/export/customs/generate', d),
  submitDeclaration:   (id) => api.post(`/supplier/export/customs/${id}/submit`),
}

export const adminInternationalApi = {
  overview:            ()        => api.get('/admin/international/overview'),
  countries:           ()        => api.get('/admin/international/countries'),
  updateCountry:       (id, d)   => api.put(`/admin/international/countries/${id}`, d),
  taxRules:            (p = {})  => api.get('/admin/international/tax-rules', { params: p }),
  upsertTaxRule:       (d)       => api.post('/admin/international/tax-rules', d),
  certifications:      (p = {})  => api.get('/admin/international/certifications', { params: p }),
  verifyCert:          (id)      => api.post(`/admin/international/certifications/${id}/verify`),
  customsDeclarations: (p = {})  => api.get('/admin/international/customs', { params: p }),
  clearDeclaration:    (id, d)   => api.post(`/admin/international/customs/${id}/clear`, d),
  holdDeclaration:     (id)      => api.post(`/admin/international/customs/${id}/hold`),
}

// ── M7: Platform OS ───────────────────────────────────────────────────────────
export const tenantWebhooksApi = {
  list:       ()       => api.get('/tenant/webhooks'),
  eventTypes: ()       => api.get('/tenant/webhooks/event-types'),
  register:   (d)      => api.post('/tenant/webhooks', d),
  show:       (id)     => api.get(`/tenant/webhooks/${id}`),
  update:     (id, d)  => api.put(`/tenant/webhooks/${id}`, d),
  remove:     (id)     => api.delete(`/tenant/webhooks/${id}`),
  ping:       (id)     => api.post(`/tenant/webhooks/${id}/ping`),
  deliveries: (id)     => api.get(`/tenant/webhooks/${id}/deliveries`),
}

export const platformOsApi = {
  dashboard:        ()                => api.get('/admin/platform/dashboard'),
  health:           (p = {})          => api.get('/admin/platform/health', { params: p }),
  events:           (p = {})          => api.get('/admin/platform/events', { params: p }),
  auditTrail:       (type, id)        => api.get(`/admin/platform/events/${type}/${id}/audit-trail`),
  emitTestEvent:    (d)               => api.post('/admin/platform/events/emit', d),
  featureFlags:     ()                => api.get('/admin/platform/feature-flags'),
  upsertFlag:       (d)               => api.post('/admin/platform/feature-flags', d),
  seedFlags:        ()                => api.post('/admin/platform/feature-flags/seed'),
  toggleFlag:       (key, value)      => api.patch(`/admin/platform/feature-flags/${key}/toggle`, { value }),
  setRollout:       (key, pct)        => api.patch(`/admin/platform/feature-flags/${key}/rollout`, { percentage: pct }),
  tenantOverride:   (key, tid, value) => api.patch(`/admin/platform/feature-flags/${key}/tenant-override`, { tenant_id: tid, value }),
  adminWebhooks:    (p = {})          => api.get('/admin/platform/webhooks', { params: p }),
  webhookDeliveries:(id)              => api.get(`/admin/platform/webhooks/${id}/deliveries`),
  pauseWebhook:     (id)              => api.post(`/admin/platform/webhooks/${id}/pause`),
  resumeWebhook:    (id)              => api.post(`/admin/platform/webhooks/${id}/resume`),
  dispatchNow:      ()                => api.post('/admin/webhooks/dispatch-now'),
  retryDelivery:    (id)              => api.post(`/admin/webhooks/deliveries/${id}/retry`),
}

// ── M8: Growth Engine ─────────────────────────────────────────────────────────
export const promotionsApi = {
  publicList: ()  => api.get('/promotions/public'),
  validate:   (d) => api.post('/promotions/validate', d),
  flashSales: ()  => api.get('/flash-sales'),
}

export const adminGrowthApi = {
  dashboard:           ()         => api.get('/admin/growth/dashboard'),
  reviews:             (p = {})   => api.get('/admin/growth/reviews', { params: p }),
  moderateReview:      (id, d)    => api.patch(`/admin/growth/reviews/${id}/moderate`, d),
  promotions:          (p = {})   => api.get('/admin/growth/promotions', { params: p }),
  createPromotion:     (d)        => api.post('/admin/growth/promotions', d),
  updatePromotion:     (id, d)    => api.put(`/admin/growth/promotions/${id}`, d),
  deactivatePromotion: (id)       => api.delete(`/admin/growth/promotions/${id}`),
  flashSales:          (p = {})   => api.get('/admin/growth/flash-sales', { params: p }),
  createFlashSale:     (d)        => api.post('/admin/growth/flash-sales', d),
  cancelFlashSale:     (id)       => api.delete(`/admin/growth/flash-sales/${id}`),
  analytics:           ()         => api.get('/admin/growth/analytics'),
  supplierAnalytics:   (id, days) => api.get(`/admin/growth/analytics/supplier/${id}?days=${days}`),
}
